import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { Buttons, Inputs } from "components";

// field ของผู้จอง -> field ของข้อมูลสมาชิก (authenRedux)
const MEMBER_FIELD_MAP = {
  first_name: "first_name",
  last_name: "last_name",
  email: "email",
  phone: "phone_number",
};

// S3 ข้อมูลผู้จอง: โรงเรียนไม่บังคับ ("ไม่ระบุ" = บุคคลทั่วไป GEN) ใบกำกับภาษี 4 ช่องตาม design
// ชื่อผู้ชมกรอกในหน้าเลือกที่นั่งแล้ว ส่งขึ้น server พร้อมฟอร์มนี้
const BookerInfoStep = ({ draft, info, memberInfo, viewerNames, onBack, onNext }) => {
  const { t } = useTranslation(["seat", "common"]);
  const schema = yup.object().shape({
    first_name: yup.string().required("required"),
    last_name: yup.string().required("required"),
    email: yup.string().email("Invalid email").required("required"),
    phone: yup
      .string()
      .required("required")
      .matches(/^0\d{8,9}$/, "Invalid phone number"),
  });

  const booker = draft.booker || {};
  const tax = draft.tax_invoice || {};
  const schoolOptions = info.schools.map((s) => ({
    value: s.code,
    label: s.code === "GEN" ? t("Not specified", { ns: "seat" }) : `${s.code} · ${s.name}`,
  }));
  const initialSchool = schoolOptions.find((o) => o.value === (draft.school_code || "GEN")) || schoolOptions[0];

  const { control, handleSubmit, setValue, clearErrors, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: booker.first_name || "",
      last_name: booker.last_name || "",
      email: booker.email || "",
      phone: booker.phone || "",
    },
  });

  const [_isSameAsMember, _setIsSameAsMember] = useState(false);
  const [_school, _setSchool] = useState(initialSchool);
  const [_isTaxRequired, _setIsTaxRequired] = useState(!!tax.is_required);
  const [_tax, _setTax] = useState({
    name: tax.name || "",
    branch: tax.branch || "",
    tax_id: tax.tax_id || "",
    address: tax.address || "",
  });
  const [_taxErrors, _setTaxErrors] = useState({});

  // ติ๊ก = เติมข้อมูลสมาชิก เอาติ๊กออก = ล้าง (แบบเดียวกับฟอร์มผู้จองห้อง)
  const _toggleSameAsMember = (checked) => {
    _setIsSameAsMember(checked);
    Object.entries(MEMBER_FIELD_MAP).forEach(([formKey, memberKey]) => {
      setValue(formKey, checked ? (memberInfo && memberInfo[memberKey]) || "" : "");
    });
    clearErrors(Object.keys(MEMBER_FIELD_MAP));
  };

  const _setTaxField = (key) => (e) => {
    _setTax((prev) => ({ ...prev, [key]: e.target.value }));
    _setTaxErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const _onSubmit = (values) => {
    if (_isTaxRequired) {
      const nextErrors = {};
      if (!_tax.name.trim()) nextErrors.name = "required";
      if (!_tax.address.trim()) nextErrors.address = "required";
      if (_tax.tax_id && !/^\d{13}$/.test(_tax.tax_id)) nextErrors.tax_id = "Tax ID must be 13 digits";
      if (Object.keys(nextErrors).length) {
        _setTaxErrors(nextErrors);
        return;
      }
    }
    onNext({
      booker: values,
      school_code: _school ? _school.value : "GEN",
      seats: draft.seats.map((s) => ({
        seat_id: s.seat_id,
        viewer_name: (viewerNames && viewerNames[s.seat_id]) || s.viewer_name || "",
      })),
      tax_invoice: { is_required: _isTaxRequired, ..._tax },
    });
  };

  const textField = (name, labelKey, extra = {}) => (
    <div className="form_row">
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Inputs.TextField
            theme_standard
            {...field}
            {...extra}
            label={t(labelKey, { ns: "seat" })}
            isRequire
            errors={errors[name]?.message}
            onChange={(e) => {
              field.onChange(e);
              _setIsSameAsMember(false);
            }}
          />
        )}
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit(_onSubmit)}>
      <div className="form_row">
        <Inputs.InputCheckBox
          theme_standard
          label={t("Same as member information", { ns: "seat" })}
          value={_isSameAsMember}
          onChange={_toggleSameAsMember}
        />
      </div>
      <div className="form_row">
        <Inputs.DropdownSelect
          theme_standard
          label={t("Music school", { ns: "seat" })}
          options={schoolOptions}
          value={_school}
          onChange={_setSchool}
        />
      </div>
      {textField("first_name", "First name")}
      {textField("last_name", "Last name")}
      {textField("email", "Email", { type: "email" })}
      {textField("phone", "Phone number", { type: "tel", inputMode: "numeric" })}

      <div className="form_row">
        <Inputs.InputCheckBox
          theme_standard
          label={t("I need a tax invoice", { ns: "seat" })}
          value={_isTaxRequired}
          onChange={_setIsTaxRequired}
        />
      </div>
      {_isTaxRequired && (
        <>
          <div className="form_row">
            <Inputs.TextField theme_standard label={t("Name / Company name", { ns: "seat" })} isRequire value={_tax.name} errors={_taxErrors.name} onChange={_setTaxField("name")} />
          </div>
          <div className="form_row">
            <Inputs.TextField theme_standard label={t("Last name / Branch", { ns: "seat" })} value={_tax.branch} onChange={_setTaxField("branch")} />
          </div>
          <div className="form_row">
            <Inputs.TextField theme_standard label={t("Tax ID (13 digits)", { ns: "seat" })} inputMode="numeric" maxLength={13} value={_tax.tax_id} errors={_taxErrors.tax_id} onChange={_setTaxField("tax_id")} />
          </div>
          <div className="form_row">
            <Inputs.TextField theme_standard label={t("Address for tax invoice", { ns: "seat" })} isRequire value={_tax.address} errors={_taxErrors.address} onChange={_setTaxField("address")} />
          </div>
        </>
      )}
      <div className="pdpa_note">
        * {t("This information is used only for the booking and tax invoice, according to the company's PDPA policy.", { ns: "seat" })}
      </div>
      <div className="btn_row">
        <div>
          <Buttons.BgStandard theme_white_purple label={t("Back", { ns: "seat" })} onClick={onBack} />
        </div>
        <div>
          <Buttons.BgStandard type="submit" label={t("Next", { ns: "seat" })} />
        </div>
      </div>
    </form>
  );
};

export default BookerInfoStep;
